/* istanbul ignore file */

import PropTypes from 'prop-types'
import {Skeleton} from '@chakra-ui/react'
import React from 'react'
import CMSContent from '../../components/cms-content'
import Section from '../../components/section'
import {useInitialDataLayer} from '../../tracking/hooks/use-initial-data-layer'
import Seo from '../../components/seo'
import HeaderContent from '../../components/header-content'
import useCmsContent from '../../components/cms-content/hooks/use-cms-content'
import {useContentAsset} from '../../hooks/useScapiCustomApi'
import {Redirect, useParams, useLocation} from 'react-router-dom'
import {useServerContext} from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks'
import {HTTPError, HTTPNotFound} from '@salesforce/pwa-kit-react-sdk/ssr/universal/errors'
import {MAX_CACHE_AGE, STALE_WHILE_REVALIDATE} from '../../constants'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import useSlugUrlRedirect from './hooks/use-slug-url-redirect'
import {isServer} from '../../utils/utils'
import LenisProvider from '../../contexts/lenis'
import CmsModules from '../../leap/modules'
import {Chakra3Theme} from '../../leap/theme'
import {HeaderVariant} from '../../leap/components/navigation/header/header-variant'
import {getFloatingHeaderVariant} from '../../leap/utils/utils'
import {getHeaderBgFromFirstModule} from '../../leap/modules/get-header-bg-from-first-module'
import {useChakraContext} from '@chakra-ui/react3'

/**
 * Maps the content asset's floating-header flag to the variant the page asks
 * for. See `--header-floating-active` in app/leap/theme/global-css.js for what
 * the breakpoint grants.
 */
export const getHeaderVariantFromContent = (content) =>
    getFloatingHeaderVariant(content?.c_showFloatingHeader)

const Content = () => {
    const {contentId} = useParams()
    const location = useLocation()
    const system = useChakraContext()

    const {res} = useServerContext()
    if (res) {
        const cacheConfig = getConfig().app.cache
        res.set(
            'Cache-Control',
            cacheConfig?.content ||
                `s-maxage=${MAX_CACHE_AGE}, stale-while-revalidate=${STALE_WHILE_REVALIDATE}`
        )
    }

    useInitialDataLayer({pageType: 'CMS Content'})

    const {data: content, isLoading, error} = useCmsContent(contentId)
    const {
        data: modules,
        isInitialLoading: isModulesLoading,
        error: errorModules
    } = useContentAsset(
        {
            parameters: {
                assetId: contentId
            }
        },
        {enabled: Boolean(content?.c_isLeapContent)}
    )

    if (error || errorModules) {
        console.error(`Error fetching content id ${contentId}`, error || errorModules)
        const errorStatus =
            error?.response?.status ||
            error?.status ||
            errorModules?.response?.status ||
            errorModules?.status
        switch (errorStatus) {
            case 404:
                throw new HTTPNotFound(`Content Asset "${contentId}" Not Found.`)
            default:
                throw new HTTPError(errorStatus, `HTTP Error ${errorStatus} occurred.`)
        }
    }

    // redirect to slug url if it's different from the current url
    const redirectTo = useSlugUrlRedirect(isServer, contentId, content)
    if (redirectTo) {
        return <Redirect to={redirectTo} />
    }

    if (
        !contentId ||
        (!isLoading && !content) ||
        (content?.c_isLeapContent && !isModulesLoading && !modules?.length)
    ) {
        if (res) {
            res.statusCode = 404
        }
        return null
    }

    if (contentId && (isLoading || isModulesLoading)) {
        return (
            <Section>
                <Skeleton height="20px" />
            </Section>
        )
    }

    const headerBg = content?.c_isLeapContent
        ? getHeaderBgFromFirstModule(system, content.c_colorTheme, modules)
        : undefined

    return (
        <>
            <HeaderVariant
                variant={getHeaderVariantFromContent(content)}
                bg={content?.c_isLeapContent ? headerBg?.bg : undefined}
                theme={headerBg?.theme}
            />
            {!location.pathname.includes('/stokke-promotions') &&
                !location.pathname.includes('/newsletter') && <HeaderContent />}
            <Seo
                title={content?.pageTitle}
                description={content?.pageDescription}
                keywords={content?.pageKeywords}
            >
                {content?.c_pageType && <meta name="page-type" content={content?.c_pageType} />}
            </Seo>
            <Section>
                {content.c_isLeapContent ? (
                    <Chakra3Theme theme={content.c_colorTheme}>
                        <LenisProvider>
                            <CmsModules assetId={contentId} modules={modules} />
                        </LenisProvider>
                    </Chakra3Theme>
                ) : (
                    <CMSContent data-testid="content-page" id={contentId} content={content} />
                )}
            </Section>
        </>
    )
}

Content.shouldGetProps = () => false

Content.propTypes = {
    id: PropTypes.string.isRequired
}

export default Content
